import { isInTeams } from '@/utils/isInTeams';
import { UserInfoError } from '@/utils/errors/UserInfoError';
import { LoginInfoError } from '@/utils/errors/LoginInfoError';
import type {
  Environment,
  AuthContext,
  AuthContextLookup,
} from '@/services/auth/types';
import { useRootStore } from '@/store/root';
import { useAuthDefault } from '@/services/auth/composables/modules/useAuthDefault';
import { useAuthTeams } from '@/services/auth/composables/modules/useAuthTeams';
import {
  currentUserProfile,
  loggedInBasic,
  clearAuth,
} from '@/services/auth/utils/authUtils';
import { $settings } from '@/provider/settings';
import { ref } from 'vue';
import localStorageHelper from '@/helper/localStorageHelper';

const loginError = ref<string | null>(null);

const LOGIN_ERROR_MESSAGES = {
  LoginInfoError: {
    isPopupBlocked:
      'Popup was blocked by your browser. Please allow popups for this site and try again.',
    isUserCancelled: 'Login was cancelled. Please try again.',
    isTokenAcquisitionFailed:
      'Failed to acquire authentication token. Please try again.',
    isLoginFailed: 'Login failed. Please try again.',
  },
  UserInfoError: {
    isSapUserNotLinked:
      'Your user is not linked in SAP. Please contact your administrator.',
    isUnauthorized:
      'You do not have the authorization to log in to this system.',
  },
} as const;

const handleLoginError = (error: unknown): void => {
  if (error instanceof LoginInfoError) {
    const messages = LOGIN_ERROR_MESSAGES.LoginInfoError;
    loginError.value =
      (error.isPopupBlocked && messages.isPopupBlocked) ||
      (error.isUserCancelled && messages.isUserCancelled) ||
      (error.isTokenAcquisitionFailed && messages.isTokenAcquisitionFailed) ||
      (error.isLoginFailed && messages.isLoginFailed) ||
      error.message ||
      'Authentication error occurred.';
  } else if (error instanceof UserInfoError) {
    const messages = LOGIN_ERROR_MESSAGES.UserInfoError;
    loginError.value =
      (error.isSapUserNotLinked && messages.isSapUserNotLinked) ||
      (error.isUnauthorized && messages.isUnauthorized) ||
      'User information error occurred.';
  } else if (!loginError.value) {
    loginError.value = 'Unknown error';
    console.error(error);
  }
};

export const useAuth = (environment: Environment = 'default') => {
  const useEnvironment = isInTeams() ? 'teams' : environment;

  const getAuthContext = (): AuthContext => {
    const authContextLookup: AuthContextLookup = {
      teams: useAuthTeams,
      default: useAuthDefault,
    };
    return authContextLookup[useEnvironment]() ?? authContextLookup.default();
  };

  const {
    getAuthToken,
    authLogin,
    authLogout,
    getGraphToken,
    getPowerBIToken,
  } = getAuthContext();

  const isAuthenticated = async () => {
    try {
      const token = await getAuthToken?.();
      if (!token) return false;

      const currentProfile = currentUserProfile.value;
      if (!currentProfile) return false;

      const cid = localStorageHelper.getCid();
      if (!cid) return false;

      const locale = localStorageHelper.getLocale();
      if (!locale) return false;

      return true;
    } catch (error: unknown) {
      handleLoginError(error);
      return false;
    }
  };

  const login = async () => {
    try {
      if (await isAuthenticated()) {
        // when in Teams, reload login to trigger onMount and route to app
        if (isInTeams()) location.reload();
        return;
      }

      loginError.value = null;
      await authLogin?.();

      if (!loggedInBasic.value && $settings.useBasicAuth) {
        location.reload();
      }

      await useRootStore().init();
      location.reload();
    } catch (error: unknown) {
      handleLoginError(error);
    }
  };

  const logout = async () => {
    const logoutSuccess = authLogout && (await authLogout());

    if (logoutSuccess) {
      clearAuth();
      localStorageHelper.clearNonAuthData();
      const { default: router } = await import('@/router');
      await router.push({ name: 'login' });
    }
  };

  return {
    login,
    logout,
    getToken: getAuthToken, // check if this is correct
    isAuthenticated,
    loginError,
    getGraphToken,
    getPowerBIToken,
  };
};
